import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuthStore } from '../stores/authStore';
import { SSOLoginButton } from '../pages/Auth';
import { Avatar } from './Avatar';
import { FaSun, FaMoon, FaSignOutAlt, FaUser, FaBars } from 'react-icons/fa';

interface NavbarProps {
  projectName: string;
  onMobileMenuToggle?: () => void;
}

export const NavbarComponent = ({ projectName, onMobileMenuToggle }: NavbarProps) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setIsProfileOpen(false);
  };

  const currentUser = user
    ? {
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        role: user.role,
        avatar: user.photo,
        initials: `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}` || user.email?.[0] || 'U',
      }
    : null;

  return (
    <header className="sticky top-0 z-50 border-b bg-white dark:bg-[var(--color-background-paper)]" style={{ borderColor: 'var(--color-border-light)' }}>
      <div className="flex w-full items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          {onMobileMenuToggle && (
            <button
              onClick={onMobileMenuToggle}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
              type="button"
            >
              <FaBars className="h-5 w-5 text-[var(--color-text-primary)]" />
            </button>
          )}
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text-primary)]">
            <div className="w-8 h-8 rounded-lg bg-[var(--color-primary-500)] inline-flex items-center justify-center text-sm font-bold text-white">
              P
            </div>
            <span>{projectName}</span>
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            type="button"
          >
            {isDarkMode ? (
              <FaSun className="h-5 w-5 text-[var(--color-text-primary)]" />
            ) : (
              <FaMoon className="h-5 w-5 text-[var(--color-text-primary)]" />
            )}
          </button>
          {isAuthenticated && currentUser ? (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsProfileOpen(!isProfileOpen);
                  }
                }}
                className="cursor-pointer p-1 border-none bg-transparent"
                aria-label="Toggle profile menu"
                type="button"
              >
                <Avatar
                  size="sm"
                  src={currentUser.avatar}
                  alt={currentUser.name}
                  className="shadow-none"
                >
                  {currentUser.initials}
                </Avatar>
              </button>

              {isProfileOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-64 rounded-lg shadow-lg border z-50"
                  style={{
                    backgroundColor: 'var(--color-background-paper)',
                    borderColor: 'var(--color-border-light)',
                  }}
                >
                  <div className="p-4 border-b" style={{ borderColor: 'var(--color-border-light)' }}>
                    <div className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {currentUser.name}
                    </div>
                    <div className="text-xs font-medium mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      {currentUser.email}
                    </div>
                  </div>

                  <div className="py-2">
                    <button
                      onClick={handleProfileClick}
                      className="w-full flex items-center px-4 py-2 text-sm font-medium transition-colors"
                      style={{
                        color: 'var(--color-text-primary)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-hover-background)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      type="button"
                    >
                      <FaUser className="h-4 w-4 mr-3" style={{ color: 'var(--color-text-secondary)' }} />
                      Profile
                    </button>
                    <div
                      className="border-t my-2"
                      style={{ borderColor: 'var(--color-border-light)' }}
                    />
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2 text-sm font-medium transition-colors"
                      style={{
                        color: 'var(--color-text-primary)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-hover-background)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      type="button"
                    >
                      <FaSignOutAlt className="h-4 w-4 mr-3" style={{ color: 'var(--color-text-secondary)' }} />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <SSOLoginButton variant="primary" size="sm" />
          )}
        </div>
      </div>
    </header>
  );
};